import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, fetchRoots, listTrash, purgeTrash, restoreEntries, type TrashEntry } from '../api'
import { formatBytes, formatDate, parentPath } from '../format'

/**
 * 지운 것들을 들여다보는 자리.
 *
 * 지운 직후의 "실행 취소" 배너는 그 화면을 떠나면 사라진다. 그것만 있을 때는 배너를
 * 놓친 파일이 보관 기간이 끝날 때까지 손댈 수 없는 채로 남았다 — 목록에도 경로에도
 * 나오지 않지만 용량은 그대로 잡고서.
 *
 * 휴지통은 노드마다 있으므로 저장소를 하나 골라 본다. 하나뿐이면 고를 것이 없다.
 */
export default function Trash() {
  const [picked, setPicked] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const roots = useQuery({ queryKey: ['roots'], queryFn: fetchRoots })

  const rootId = picked ?? roots.data?.[0]?.id ?? null
  const root = roots.data?.find((candidate) => candidate.id === rootId)

  const trash = useQuery({
    queryKey: ['trash', rootId],
    queryFn: () => listTrash(rootId!),
    enabled: rootId !== null,
  })

  /**
   * 되돌린 파일은 제 폴더에 다시 나타나야 하고, 영구히 지운 것은 용량에서 빠져야 한다.
   * 어느 폴더를 열어두고 있었는지 여기서는 알 수 없으므로 전부 다시 읽는다.
   */
  function refresh() {
    setFailure(null)
    void queryClient.invalidateQueries()
  }

  const restore = useMutation({
    mutationFn: (item: TrashEntry) => restoreEntries(rootId!, [item.id]),
    onSuccess: refresh,
    onError: (err: unknown) => setFailure(`되돌리지 못했습니다 — ${reason(err)}`),
  })

  const purge = useMutation({
    mutationFn: (item: TrashEntry) => purgeTrash(rootId!, [item.id]),
    onSuccess: refresh,
    onError: (err: unknown) => setFailure(`지우지 못했습니다 — ${reason(err)}`),
  })

  const busy = restore.isPending || purge.isPending
  const canAct = root?.permission === 'write' && root.online

  return (
    <div className="pad">
      <h1>휴지통</h1>
      <p className="muted small">
        지운 것은 같은 디스크의 휴지통으로 옮겨집니다. 목록에도 경로에도 나오지 않지만 바이트는
        아직 디스크에 있어서 용량에는 그대로 잡힙니다. 노드에 설정된 보관 기간(기본 30일)이
        지나면 에이전트가 실제로 지웁니다.
      </p>

      {roots.data && roots.data.length > 1 && (
        <div className="row-form">
          <label className="inline">
            저장소
            <select
              value={rootId ?? ''}
              onChange={(event) => {
                setPicked(event.target.value)
                setFailure(null)
              }}
            >
              {roots.data.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {roots.data?.length === 0 && <p className="muted small">사용 가능한 저장소가 없습니다.</p>}

      {root && !root.online && (
        <p className="error">
          {root.label}이(가) 오프라인입니다. 휴지통은 그 기계의 디스크에 있어서 지금은 볼 수
          없습니다.
        </p>
      )}

      {root?.online && root.permission === 'read' && (
        <p className="muted small">
          읽기 전용으로 받은 저장소라 되돌리거나 지울 수는 없습니다.
        </p>
      )}

      {trash.isError && root?.online && (
        <p className="error">휴지통을 읽지 못했습니다 — {reason(trash.error)}</p>
      )}

      {failure && <p className="error">{failure}</p>}

      <table className="entries">
        <thead>
          <tr>
            <th>이름</th>
            <th>원래 자리</th>
            <th className="num">크기</th>
            <th className="num">지운 때</th>
            <th className="actions" />
          </tr>
        </thead>
        <tbody>
          {trash.isPending && rootId !== null && (
            <tr>
              <td colSpan={5} className="muted center">
                불러오는 중…
              </td>
            </tr>
          )}
          {trash.data?.items.length === 0 && (
            <tr>
              <td colSpan={5} className="muted center">
                휴지통이 비어 있습니다.
              </td>
            </tr>
          )}
          {trash.data?.items.map((item) => (
            <tr key={item.id}>
              <td>
                {item.name}
                {item.type === 'dir' && <span className="muted small"> · 폴더</span>}
              </td>
              <td className="mono small muted">{parentPath(item.path)}</td>
              <td className="num">{formatBytes(item.size)}</td>
              <td className="num small">{formatDate(item.deletedAt)}</td>
              <td className="actions">
                {canAct && (
                  <>
                    <button
                      type="button"
                      className="link"
                      disabled={busy}
                      onClick={() => restore.mutate(item)}
                    >
                      되돌리기
                    </button>
                    {/* 이 앱에서 되돌릴 수 없는 일은 이것 하나뿐이라, 여기서만 묻는다. */}
                    <button
                      type="button"
                      className="link danger"
                      disabled={busy}
                      onClick={() => {
                        const where = parentPath(item.path)
                        if (
                          !window.confirm(
                            `${where === '/' ? '' : `${where}/`}${item.name}을(를) 지금 영구히 지웁니다. 이것만은 되돌릴 수 없습니다.`,
                          )
                        ) {
                          return
                        }
                        purge.mutate(item)
                      }}
                    >
                      영구 삭제
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * 왜 안 됐는지를 그 자리에서 읽히게.
 *
 * 되돌리기가 막히는 경우는 대개 두 가지고, 둘 다 사람이 할 수 있는 일이 있다 —
 * 그런데 에이전트가 내는 문장만으로는 무엇을 하라는 것인지 알 수 없다.
 */
function reason(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return '그 자리에 이미 다른 것이 있습니다. 그것을 옮기거나 이름을 바꾼 뒤에 다시 해보세요.'
  }
  if (err instanceof ApiError && err.status === 410) {
    return '알맹이가 이미 없습니다. 보관 기간이 지나 치워진 것 같습니다.'
  }
  return err instanceof Error ? err.message : String(err)
}
